import Layout from "../components/Layout";
import { ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    title: "Responsable",
    blocks: [
      { type: "dl", items: [
        ["Responsable", "Ariza Santiago Wealth Solutions, S.L."],
        ["NIF", "B72350325"],
        ["Marca / plataforma", "Relevo"],
        ["Domicilio de contacto", "Paseo de la Castellana 200, 28046 Madrid"],
        ["Correo electrónico", "info@mascorporate.net"],
      ] },
    ],
  },
  {
    title: "Qué datos tratamos",
    blocks: [
      { type: "p", text: "Relevo puede tratar datos identificativos y de contacto, datos profesionales y societarios, información incluida en perfiles de usuario, información relativa a empresas y operaciones, comunicaciones mantenidas mediante la plataforma, documentación aportada durante los procesos, datos de contratación electrónica y datos técnicos o de seguridad necesarios para operar la cuenta." },
      { type: "p", text: "Recomendamos no incorporar categorías especiales de datos personales salvo que sean estrictamente necesarias." },
    ],
  },
  {
    title: "Para qué utilizamos los datos y con qué base jurídica",
    blocks: [
      { type: "p", text: "Los datos se utilizarán para crear y gestionar cuentas, verificar y cualificar usuarios, prestar las funcionalidades de Relevo, conectar compradores y vendedores, gestionar NDAs y Mandatos M&A, desarrollar potenciales operaciones, mantener la seguridad de la plataforma y acreditar las relaciones contractuales." },
      { type: "p", text: "Estos tratamientos se basan principalmente en la ejecución de la relación contractual o en actuaciones precontractuales solicitadas por el usuario, en el cumplimiento de obligaciones legales y, cuando corresponda, en los intereses legítimos de Relevo relacionados con seguridad, prevención del fraude y defensa de derechos." },
      { type: "p", text: "Las comunicaciones comerciales no necesarias para prestar el servicio se realizarán únicamente cuando exista una base jurídica suficiente y podrán cancelarse en cualquier momento." },
    ],
  },
  {
    title: "Matching, scoring e inteligencia artificial",
    blocks: [
      { type: "p", text: "Relevo podrá utilizar herramientas automatizadas o de inteligencia artificial para ayudar a analizar perfiles, priorizar oportunidades, realizar matching, estructurar información y asistir a los usuarios durante una operación." },
      { type: "p", text: "Estas herramientas podrán generar recomendaciones o puntuaciones internas." },
      { type: "p", text: "Relevo no adoptará decisiones basadas exclusivamente en tratamientos automatizados que produzcan efectos jurídicos o significativamente similares sobre una persona sin las garantías que legalmente correspondan. Los procesos de admisión y las decisiones relevantes podrán someterse a revisión humana." },
    ],
  },
  {
    title: "Con quién se comparten los datos",
    blocks: [
      { type: "p", text: "La información podrá comunicarse a otros usuarios cuando ello sea necesario para desarrollar una oportunidad y exista la autorización o base contractual correspondiente." },
      { type: "p", text: "Relevo podrá asimismo utilizar proveedores tecnológicos, servicios de alojamiento, comunicaciones, firma o registro electrónico, análisis de datos, inteligencia artificial y otros proveedores necesarios para la operación de la plataforma, sujetos a las obligaciones contractuales de protección de datos que correspondan." },
      { type: "p", text: "También podrán comunicarse datos a asesores profesionales, autoridades u organismos públicos cuando resulte necesario o legalmente obligatorio." },
    ],
  },
  {
    title: "Transferencias internacionales",
    blocks: [
      { type: "p", text: "Cuando un proveedor procese datos fuera del Espacio Económico Europeo, Relevo aplicará las garantías exigidas por el RGPD, como decisiones de adecuación, cláusulas contractuales tipo u otros mecanismos legalmente previstos." },
    ],
  },
  {
    title: "Conservación",
    blocks: [
      { type: "p", text: "Los datos de la cuenta se conservarán mientras permanezca activa y posteriormente durante los plazos necesarios para atender posibles responsabilidades legales o contractuales." },
      { type: "p", text: "Los contratos, registros de operaciones y datos necesarios para acreditar derechos y obligaciones podrán conservarse bloqueados durante los plazos legales de prescripción correspondientes." },
      { type: "p", text: "Los datos utilizados exclusivamente para comunicaciones comerciales se conservarán hasta que el interesado retire su consentimiento o se oponga a su tratamiento." },
    ],
  },
  {
    title: "Derechos",
    blocks: [
      { type: "p", text: "Los interesados podrán solicitar el acceso, rectificación, supresión, oposición, limitación y, cuando proceda, portabilidad de sus datos, así como retirar los consentimientos otorgados." },
      { type: "p", text: "Las solicitudes podrán dirigirse a info@mascorporate.net, acreditando la identidad cuando resulte necesario." },
      { type: "p", text: "También podrá presentarse una reclamación ante la Agencia Española de Protección de Datos." },
    ],
  },
  {
    title: "Seguridad",
    blocks: [
      { type: "p", text: "Relevo adoptará medidas técnicas y organizativas adecuadas al riesgo para proteger los datos frente a pérdida, acceso no autorizado, alteración o divulgación." },
    ],
  },
  {
    title: "Cambios en esta política",
    blocks: [
      { type: "p", text: "La Política de Privacidad podrá actualizarse para reflejar modificaciones de la plataforma o de la normativa aplicable. Cuando el cambio sea relevante se informará adecuadamente a los usuarios." },
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 lg:py-20" data-testid="privacy-policy-page">
        <div className="mb-12 fade-up">
          <span className="font-mono-label text-gold flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Legal · Protección de datos
          </span>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3">Política de Privacidad</h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            Información sobre cómo Relevo trata los datos personales de los usuarios de la plataforma, conforme al Reglamento (UE) 2016/679 (RGPD) y la normativa española aplicable.
          </p>
        </div>

        <div className="hairline bg-card">
          {SECTIONS.map((s, i) => (
            <section key={i} className={`grid md:grid-cols-12 gap-4 px-6 md:px-8 py-7 ${i > 0 ? "hairline-t" : ""}`} data-testid={`privacy-section-${i + 1}`}>
              <div className="md:col-span-4">
                <span className="font-mono-label text-[#E0712C]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif-display text-xl leading-tight mt-1">{s.title}</h2>
              </div>
              <div className="md:col-span-8 space-y-3">
                {s.blocks.map((b, j) =>
                  b.type === "dl" ? (
                    <dl key={j} className="grid sm:grid-cols-[180px_1fr] gap-y-2 gap-x-4 text-sm">
                      {b.items.map(([k, v]) => (
                        <div key={k} className="contents">
                          <dt className="font-mono-label text-muted-foreground">{k}</dt>
                          <dd className="text-foreground">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p key={j} className="text-sm md:text-base text-muted-foreground leading-relaxed">{b.text}</p>
                  )
                )}
              </div>
            </section>
          ))}
        </div>

        <p className="font-mono-label text-muted-foreground mt-8">Última actualización: junio de 2026</p>
      </div>
    </Layout>
  );
}
